<template>
  <div
    :class="
      'alert d-flex align-items-center w-100 alert-light-' + type + ' ' + width + (clickable ? ' -clickable' : '')
    "
    :role="clickable ? 'button' : role"
    :tabindex="clickable ? 0 : null"
    @click="handleAlertClick"
    @keydown.enter="handleAlertKeydown"
    @keydown.space="handleAlertKeydown"
  >
    <FontAwesomeIcon v-if="icon" :icon="'fa-light fa-' + icon" aria-hidden="true" />
    <div :class="{ 'ms-2': !!icon }">
      <div class="fw-bold" v-if="title">{{ title }}</div>
      <!-- ST143-TI-17771 (c-group 3, KEEP + SANITIZE): shared cross-cutting component (~18 call sites). Most pass
         pure i18n (safe), but ContractCardComponent.vue's alertHtml computed concatenates contract.ruptureCause
         and contract.ruptureCauseUserText — a field literally named "user text" — into markup. Sanitized here,
         once, since the prop is shared by every caller. -->
      <slot><div class="mb-0" v-safe-html="text"></div></slot>
    </div>
    <slot name="action" />
    <div v-if="actions && actions.length" :class="{ 'ms-auto align-self-start': actionsEnd }">
      <button
        v-for="action in actions"
        :key="action.action"
        v-tooltip="{ title: action.tooltip }"
        :class="'ms-2 me-0 btn btn-icon -' + type"
        @click="handleClick(action)"
      >
        <FontAwesomeIcon v-if="action.icon" :icon="'fa-light fa-' + action.icon" aria-hidden="true" />
      </button>
    </div>
    <router-link
      v-if="alertLink && Object.keys(alertLink).length"
      :to="{ name: alertLink.name, params: { alias: alertLink.alias } }"
      @click="cancel"
      >{{ alertLink.text }}</router-link
    >
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  name: "AlertBlock",
  emits: ["cancel", "click"],
  components: { FontAwesomeIcon },
  props: {
    type: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: false,
      default: "",
    },
    text: {
      type: String,
      required: false,
      default: "",
    },
    width: {
      type: String,
      required: false,
      default: "",
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    actions: {
      type: Object,
      required: false,
      default: null,
    },
    actionsEnd: {
      type: Boolean,
      required: false,
      default: false,
    },
    role: {
      type: String,
      required: false,
      default: null,
      validator: (v) => v === null || ["alert", "status", "alertdialog"].includes(v),
    },
    alertLink: {
      type: Object,
      required: false,
      default: null,
    },
    // ST154-TA-4911 (cf. feedback_reuse_alert_pattern). Les handlers click/keydown ignorent les
    // événements originaires d'un enfant interactif (actions/alertLink) : cible réelle vs cible
    // d'écoute comparées explicitement, pas un simple commentaire d'intention.
    clickable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    handleClick(action) {
      this.$emit(action.action);
    },
    handleAlertClick(event) {
      if (!this.clickable || event.target.closest("button, a")) {
        return;
      }
      this.$emit("click");
    },
    handleAlertKeydown(event) {
      if (!this.clickable || event.target !== event.currentTarget) {
        return;
      }
      event.preventDefault();
      this.$emit("click");
    },
    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
.alert {
  width: fit-content;

  &__icon {
    font-size: 16px;
    margin-right: 4px;
  }

  // Le reset global `:focus { outline: none; }` (main.scss) ne laisse aucune trace de focus
  // visible sur ce rôle "button" ad hoc : on lui redonne un contour propre, à l'image de
  // .contractActivity__title (ContractActivityComponent.vue).
  &.-clickable {
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }
  }
}

.alert-light-white {
  border: 1px solid $m_color_6;
}

.btn-icon {
  &.-warning {
    color: #f8a007;
  }
  &.-danger {
    color: #da5164;
  }
  &.-success {
    color: #6ac7a4;
  }

  svg {
    height: 1em;
  }
}
</style>
